import React from 'react';
import styled from 'styled-components';
import toggleIcon from '../../asset/icon/toggle.png';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { MemberInfoValue } from '../../shared/component/headless/input/InputBody';
import { CareerData } from '../../shared/dto/EntityRepository';
import {
  AboutContentMode,
  CareerPeriodType,
} from '../../shared/enum/EnumRepository';
import {
  DEFAULT_CAREER_CURRENT_LABEL,
  getCareerCurrentLabel,
} from '../../shared/util/careerCurrentLabel';
import {
  getCareerEndDateDisplay,
  getCareerStartDateDisplay,
} from '../../shared/util/formatCareerDate';
import CareerDateInput from './CareerDateInput';
import MoleculeValue from './molecules/MoleculeValue';

interface CareerDateSectionProps {
  data: CareerData;
  displayAsPeriod: boolean;
  isEditMode: boolean;
  isExtended: boolean;
  contentMode: AboutContentMode;
  onToggleCurrent: (isCurrent: boolean) => void;
}

const CareerDateSection: React.FC<CareerDateSectionProps> = ({
  data,
  displayAsPeriod,
  isEditMode,
  isExtended,
  contentMode,
  onToggleCurrent,
}) => {
  const periodData =
    displayAsPeriod && data.periodType === CareerPeriodType.PERIOD
      ? data
      : null;
  const startDateDisplay = getCareerStartDateDisplay(
    data.startYear,
    data.startMonth,
    data.startDay,
    { isEditMode, isExtended },
  );

  if (!periodData) {
    return (
      <PointSection>
        {isEditMode && isExtended ? (
          <CareerDateInput
            careerId={data.id}
            year={data.startYear}
            month={data.startMonth}
            day={data.startDay}
            range="start"
            variant="point"
          />
        ) : (
          <MoleculeValue
            careerId={data.id}
            value={startDateDisplay}
            targetField="startYear"
            inputStyle={MemberInfoValue}
            StyledDiv={Year}
            placeholder="YEAR"
          />
        )}
      </PointSection>
    );
  }

  const currentLabel = getCareerCurrentLabel(
    periodData.currentLabelOrig,
    contentMode,
  );
  const endDateDisplay = getCareerEndDateDisplay(
    periodData.endYear,
    periodData.endMonth,
    periodData.endDay,
    { isEditMode, isExtended },
  );
  const periodSectionGap =
    isEditMode || periodData.isCurrent || endDateDisplay !== '';
  const showCompactSeparator =
    !isExtended &&
    (
      isEditMode ||
      Boolean(
        periodData.startYear &&
        (
          periodData.endYear ||
          (periodData.isCurrent && currentLabel)
        ),
      )
    );

  return (
    <PeriodSection
      $isExtended={isExtended}
      $isEditMode={isEditMode}
      $gap={periodSectionGap}
    >
      {isEditMode && isExtended ? (
        <CareerDateInput
          careerId={periodData.id}
          year={periodData.startYear}
          month={periodData.startMonth}
          day={periodData.startDay}
          range="start"
          variant="period"
        />
      ) : (
        <MoleculeValue
          careerId={periodData.id}
          value={startDateDisplay}
          targetField="startYear"
          inputStyle={YearInputStyle}
          StyledDiv={Year}
          placeholder="Year"
        />
      )}
      {showCompactSeparator && (
        <Dash>-</Dash>
      )}
      <EndDateSection $isExtended={isExtended}>
        {periodData.isCurrent ? (
          contentMode === AboutContentMode.Orig ? (
            <MoleculeValue
              careerId={periodData.id}
              value={isEditMode ? periodData.currentLabelOrig : currentLabel}
              targetField="currentLabelOrig"
              inputStyle={CurrentLabelInputStyle}
              StyledDiv={Year}
              placeholder=""
            />
          ) : (
            <Year>{DEFAULT_CAREER_CURRENT_LABEL}</Year>
          )
        ) : (
          isEditMode && isExtended ? (
            <CareerDateInput
              careerId={periodData.id}
              year={periodData.endYear}
              month={periodData.endMonth}
              day={periodData.endDay}
              range="end"
              variant="period"
              prefix="~"
            />
          ) : (
            <MoleculeValue
              careerId={periodData.id}
              value={endDateDisplay}
              targetField="endYear"
              inputStyle={YearInputStyle}
              StyledDiv={Year}
              placeholder="Year"
            />
          )
        )}
        {isEditMode && (
          <UniversalIconBtn
            size="small"
            border={false}
            isTransparent
            backgroundImg={toggleIcon}
            aria-label={DEFAULT_CAREER_CURRENT_LABEL}
            aria-pressed={periodData.isCurrent}
            onClick={() => onToggleCurrent(!periodData.isCurrent)}
          />
        )}
      </EndDateSection>
    </PeriodSection>
  );
};

const PointSection = styled.div`
  width: clamp(80px, 12vw, 90px);
  display: flex;
  ${({ theme }) => theme.typography.Body_04};
`;

const PeriodSection = styled.div<{
  $isExtended: boolean;
  $isEditMode: boolean;
  $gap: boolean;
}>`
  width: ${({ $isEditMode }) =>
    $isEditMode
      ? 'clamp(110px, 12vw, 125px)'
      : 'clamp(80px, 12vw, 90px)'};
  flex-shrink: 0;
  display: flex;
  flex-direction: ${({ $isExtended }) => $isExtended ? 'column' : 'row'};
  gap: ${({ $gap }) => $gap ? '6px' : '0px'};
  ${({ theme }) => theme.typography.Body_04};
  transition: width 0.3s ease-in-out;
`;

const Year = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
`;

const YearInputStyle = styled.input`
  width: clamp(30px, 3vw, 40px);
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  background: transparent;
  outline: none;
  padding: 2px 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const EndDateSection = styled.div<{ $isExtended: boolean }>`
  width: ${({ $isExtended }) => $isExtended ? '100%' : 'fit-content'};
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CurrentLabelInputStyle = styled(YearInputStyle)`
  width: clamp(30px, 3vw, 40px);
`;

const Dash = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  ${({ theme }) => theme.typography.Body_04};
`;

export default CareerDateSection;
